import {
  useEffect,
  useMemo,
  useRef,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { Minus, Plus } from "lucide-react";

import type {
  DatabookPreviewBorderSide,
  DatabookPreviewCell,
  DatabookPreviewStyle,
  DatabookSheetPreview,
} from "@/lib/api/databook";
import {
  databookCellSelectionBounds,
  excelColumnName,
  extendDatabookCellSelection,
  isCellInDatabookSelection,
  startDatabookCellSelection,
  type DatabookCellSelection,
  type DatabookCellSelectionBounds,
} from "./databookCellSelection";

interface DatabookSheetGridProps {
  sheet: DatabookSheetPreview;
  outlineLoading?: boolean;
  selection?: DatabookCellSelection | null;
  onSelectionChange?: (selection: DatabookCellSelection | null) => void;
  onToggleOutlineGroup?: (groupId: string) => void;
  onToggleColumnOutlineGroup?: (groupId: string) => void;
}

const ignoreSelectionChange = () => undefined;
const ignoreOutlineToggle = () => undefined;
const REPORT_TRAILING_COLUMNS = 4;
const REPORT_TRAILING_ROWS = 10;

const DEFAULT_STYLE: DatabookPreviewStyle = {
  id: 0,
  font: {
    name: null,
    size: null,
    color: null,
    bold: false,
    italic: false,
    underline: false,
  },
  fill: { color: null },
  border: {
    top: { style: null, color: null },
    right: { style: null, color: null },
    bottom: { style: null, color: null },
    left: { style: null, color: null },
  },
  alignment: {
    horizontal: null,
    vertical: null,
    wrap_text: false,
    indent: 0,
  },
  number_format: "General",
};

function coordinate(row: number, column: number): string {
  return `${row}:${column}`;
}

function borderCss(side: DatabookPreviewBorderSide): string | undefined {
  if (!side.style) return undefined;
  const width = side.style === "thick"
    ? 3
    : side.style.startsWith("medium") || side.style === "double"
      ? 2
      : 1;
  const style = side.style === "double"
    ? "double"
    : side.style.includes("dash")
      ? "dashed"
      : side.style === "dotted" || side.style === "hair"
        ? "dotted"
        : "solid";
  return `${width}px ${style} ${side.color ?? "#000000"}`;
}

function horizontalAlignment(
  cell: DatabookPreviewCell | undefined,
  style: DatabookPreviewStyle,
): CSSProperties["textAlign"] {
  const horizontal = style.alignment.horizontal;
  if (horizontal === "center" || horizontal === "centerContinuous") {
    return "center";
  }
  if (horizontal === "right") return "right";
  if (horizontal === "justify" || horizontal === "distributed") {
    return "justify";
  }
  if (horizontal === "left") return "left";
  return cell?.kind === "number" || cell?.kind === "date" ? "right" : "left";
}

function verticalAlignment(
  style: DatabookPreviewStyle,
): CSSProperties["alignItems"] {
  if (style.alignment.vertical === "top") return "flex-start";
  if (style.alignment.vertical === "center") return "center";
  return "flex-end";
}

function canOverflowIntoEmptyCells(
  cell: DatabookPreviewCell | undefined,
  style: DatabookPreviewStyle,
): boolean {
  const horizontal = style.alignment.horizontal;
  return cell?.kind === "string"
    && !style.alignment.wrap_text
    && (horizontal === null || horizontal === "left" || horizontal === "general");
}

function cellCss(
  cell: DatabookPreviewCell | undefined,
  style: DatabookPreviewStyle,
  showGridLines: boolean,
  centerAcrossCells = false,
): CSSProperties {
  const allowTextOverflow = centerAcrossCells || canOverflowIntoEmptyCells(cell, style);
  return {
    alignItems: verticalAlignment(style),
    backgroundColor: style.fill.color ?? "#ffffff",
    borderTop: borderCss(style.border.top),
    borderRight:
      borderCss(style.border.right)
      ?? (showGridLines ? "1px solid #d9d9d9" : undefined),
    borderBottom:
      borderCss(style.border.bottom)
      ?? (showGridLines ? "1px solid #d9d9d9" : undefined),
    borderLeft: borderCss(style.border.left),
    color: style.font.color ?? "#111827",
    display: "flex",
    fontFamily: style.font.name ?? "Arial, sans-serif",
    fontSize: style.font.size ? `${style.font.size}pt` : "10pt",
    fontStyle: style.font.italic ? "italic" : undefined,
    fontWeight: style.font.bold ? 700 : 400,
    justifyContent: centerAcrossCells ? "flex-start" : horizontalAlignment(cell, style) === "right"
      ? "flex-end"
      : horizontalAlignment(cell, style) === "center"
        ? "center"
        : "flex-start",
    minHeight: 0,
    minWidth: 0,
    overflow: allowTextOverflow ? "visible" : "hidden",
    paddingLeft: 4 + style.alignment.indent * 12,
    paddingRight: 4,
    textAlign: horizontalAlignment(cell, style),
    textDecoration: style.font.underline ? "underline" : undefined,
    whiteSpace: style.alignment.wrap_text ? "pre-wrap" : "pre",
    wordBreak: style.alignment.wrap_text ? "break-word" : undefined,
  };
}

function selectionBoxShadow(
  bounds: DatabookCellSelectionBounds,
  row: number,
  column: number,
): string | undefined {
  const edges: string[] = [];
  if (row === bounds.minRow) edges.push("inset 0 2px #217346");
  if (row === bounds.maxRow) edges.push("inset 0 -2px #217346");
  if (column === bounds.minColumn) edges.push("inset 2px 0 #217346");
  if (column === bounds.maxColumn) edges.push("inset -2px 0 #217346");
  return edges.length > 0 ? edges.join(", ") : undefined;
}

export function DatabookSheetGrid({
  sheet,
  outlineLoading = false,
  selection = null,
  onSelectionChange = ignoreSelectionChange,
  onToggleOutlineGroup = ignoreOutlineToggle,
  onToggleColumnOutlineGroup = ignoreOutlineToggle,
}: DatabookSheetGridProps) {
  const dragging = useRef(false);
  const selectionRef = useRef(selection);
  const selectionBounds = databookCellSelectionBounds(selection, sheet.name);
  const visibleRowOffset = sheet.row_offset ?? 0;
  const visibleRowCount = sheet.returned_row_count ?? sheet.row_count;
  const trailingRowCount = sheet.row_offset === undefined
    ? REPORT_TRAILING_ROWS
    : 0;
  const trailingColumnCount = sheet.row_offset === undefined
    ? REPORT_TRAILING_COLUMNS
    : 0;
  const displayedRowCount = visibleRowCount + trailingRowCount;
  const displayedColumnCount = sheet.column_count + trailingColumnCount;

  useEffect(() => {
    selectionRef.current = selection;
  }, [selection]);

  useEffect(() => {
    const stopDragging = () => {
      dragging.current = false;
    };
    window.addEventListener("pointerup", stopDragging);
    window.addEventListener("pointercancel", stopDragging);
    window.addEventListener("blur", stopDragging);
    return () => {
      window.removeEventListener("pointerup", stopDragging);
      window.removeEventListener("pointercancel", stopDragging);
      window.removeEventListener("blur", stopDragging);
    };
  }, []);

  const handlePointerDown = (
    event: ReactPointerEvent<HTMLDivElement>,
    row: number,
    column: number,
  ) => {
    if (event.button !== 0) return;
    event.preventDefault();
    dragging.current = true;
    const next = startDatabookCellSelection(
      sheet.name,
      selectionRef.current,
      { row, column },
      event.shiftKey,
    );
    selectionRef.current = next;
    onSelectionChange(next);
  };

  const handlePointerEnter = (row: number, column: number) => {
    if (!dragging.current) return;
    if (!selectionRef.current) return;
    const next = extendDatabookCellSelection(
      selectionRef.current,
      { row, column },
    );
    selectionRef.current = next;
    onSelectionChange(next);
  };

  const layout = useMemo(() => {
    const rowHeights = Array.from(
      { length: displayedRowCount },
      () => sheet.default_row_height,
    );
    for (const row of sheet.rows) {
      const localRow = row.index - visibleRowOffset;
      if (localRow >= 0 && localRow < rowHeights.length) {
        rowHeights[localRow] = row.height;
      }
    }
    const columnWidths = Array.from(
      { length: displayedColumnCount },
      () => sheet.default_column_width,
    );
    for (const column of sheet.columns) {
      if (column.index < columnWidths.length) {
        columnWidths[column.index] = column.width;
      }
    }

    const rowOffsets: number[] = [];
    let rowOffset = 0;
    for (const height of rowHeights) {
      rowOffsets.push(rowOffset);
      rowOffset += height;
    }
    const sourceToLocalRow = new Map(
      sheet.rows.map((row) => [row.source_index, row.index - visibleRowOffset]),
    );
    const outlineGroups = sheet.outline_groups.flatMap((group) => {
      const summaryRow = sourceToLocalRow.get(group.summary_source_index);
      if (summaryRow === undefined) return [];
      let firstDetailRow: number | undefined;
      let lastDetailRow: number | undefined;
      for (const row of sheet.rows) {
        if (
          row.source_index < group.start_source_index
          || row.source_index > group.end_source_index
        ) {
          continue;
        }
        const localRow = row.index - visibleRowOffset;
        if (localRow < 0 || localRow >= rowHeights.length) continue;
        firstDetailRow ??= localRow;
        lastDetailRow = localRow;
      }
      return [{ group, firstDetailRow, lastDetailRow, summaryRow }];
    });
    const columnOffsets: number[] = [];
    let columnOffset = 0;
    for (const width of columnWidths) {
      columnOffsets.push(columnOffset);
      columnOffset += width;
    }
    const sourceToProjectedColumn = new Map(
      sheet.columns.map((column) => [column.source_index, column.index]),
    );
    const columnOutlineGroups = sheet.column_outline_groups.flatMap((group) => {
      const summaryColumn = sourceToProjectedColumn.get(group.summary_source_index);
      if (summaryColumn === undefined) return [];
      let firstDetailColumn: number | undefined;
      let lastDetailColumn: number | undefined;
      for (const column of sheet.columns) {
        if (
          column.source_index < group.start_source_index
          || column.source_index > group.end_source_index
        ) {
          continue;
        }
        firstDetailColumn ??= column.index;
        lastDetailColumn = column.index;
      }
      return [{
        group,
        firstDetailColumn,
        lastDetailColumn,
        summaryColumn,
      }];
    });
    const cells = new Map(
      sheet.cells.map((cell) => [coordinate(cell.row, cell.column), cell]),
    );
    const styles = new Map(sheet.styles.map((style) => [style.id, style]));
    const mergeAnchors = new Map<string, DatabookSheetPreview["merges"][number]>();
    const mergedChildren = new Set<string>();
    for (const merge of sheet.merges) {
      mergeAnchors.set(coordinate(merge.start_row, merge.start_column), merge);
      for (let row = merge.start_row; row <= merge.end_row; row += 1) {
        for (
          let column = merge.start_column;
          column <= merge.end_column;
          column += 1
        ) {
          if (row !== merge.start_row || column !== merge.start_column) {
            mergedChildren.add(coordinate(row, column));
          }
        }
      }
    }
    const centerAcrossRanges = new Map<string, { start: number; end: number }>();
    for (const cell of sheet.cells) {
      const key = coordinate(cell.row, cell.column);
      const style = styles.get(cell.style_id) ?? DEFAULT_STYLE;
      if (!cell.display_text || style.alignment.horizontal !== "centerContinuous"
        || mergeAnchors.has(key) || mergedChildren.has(key)) continue;
      let end = cell.column;
      for (let column = cell.column + 1; column < sheet.column_count; column += 1) {
        const nextKey = coordinate(cell.row, column);
        const nextCell = cells.get(nextKey);
        const nextStyle = styles.get(nextCell?.style_id ?? 0) ?? DEFAULT_STYLE;
        if (nextCell?.display_text || nextStyle.alignment.horizontal !== "centerContinuous"
          || mergeAnchors.has(nextKey) || mergedChildren.has(nextKey)) break;
        end = column;
      }
      const range = { start: cell.column, end };
      for (let column = range.start; column <= range.end; column += 1) {
        centerAcrossRanges.set(coordinate(cell.row, column), range);
      }
    }
    return {
      cells,
      centerAcrossRanges,
      columnOffsets,
      columnOutlineGroups,
      columnWidths,
      mergeAnchors,
      mergedChildren,
      outlineGroups,
      rowHeights,
      rowOffsets,
      styles,
      totalRowHeight: rowOffset,
      totalColumnWidth: columnOffset,
    };
  }, [displayedColumnCount, displayedRowCount, sheet, visibleRowOffset]);

  const textOverflowWidth = (
    row: number,
    column: number,
    merge: DatabookSheetPreview["merges"][number] | undefined,
  ): number => {
    const startColumn = column;
    let endColumn = merge?.end_column ?? column;
    for (
      let candidate = endColumn + 1;
      candidate < displayedColumnCount;
      candidate += 1
    ) {
      const candidateKey = coordinate(row, candidate);
      const candidateCell = layout.cells.get(candidateKey);
      if (
        candidateCell?.display_text
        || layout.mergeAnchors.has(candidateKey)
        || layout.mergedChildren.has(candidateKey)
      ) {
        break;
      }
      endColumn = candidate;
    }
    return layout.columnOffsets[endColumn]
      + layout.columnWidths[endColumn]
      - layout.columnOffsets[startColumn];
  };

  const expandedOutlineGroups = new Set(sheet.expanded_outline_groups);
  const expandedColumnOutlineGroups = new Set(
    sheet.expanded_column_outline_groups,
  );
  const outlineDepth = sheet.outline_groups.reduce(
    (maximum, group) => Math.max(maximum, group.level),
    0,
  );
  const outlineWidth = sheet.row_offset === undefined && outlineDepth > 0
    ? outlineDepth * 18 + 8
    : 0;
  const columnOutlineDepth = sheet.column_outline_groups.reduce(
    (maximum, group) => Math.max(maximum, group.level),
    0,
  );
  const columnOutlineHeight = sheet.row_offset === undefined
    && columnOutlineDepth > 0
    ? columnOutlineDepth * 18 + 8
    : 0;
  const canvasWidth = layout.totalColumnWidth;
  const canvasHeight = layout.totalRowHeight;
  const availableGridHeight = columnOutlineHeight > 0
    ? `calc(100% - ${columnOutlineHeight}px)`
    : "100%";

  if (visibleRowCount === 0 || sheet.column_count === 0) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        This sheet is empty.
      </div>
    );
  }

  return (
    <div
      className="h-full w-full overflow-auto bg-white"
      data-testid="databook-sheet-grid"
    >
      <div
        className="flex w-max min-w-full bg-[#f3f3f3]"
        style={{
          minHeight: `max(100%, ${Math.max(
            canvasHeight + columnOutlineHeight,
            1,
          )}px)`,
        }}
      >
        {outlineWidth > 0 && (
          <div
            aria-label="Row outline"
            className="sticky left-0 z-20 shrink-0 border-r border-[#b7b7b7] bg-[#f3f3f3]"
            style={{ marginTop: columnOutlineHeight, width: outlineWidth }}
          >
            {layout.outlineGroups.map(({
              group,
              firstDetailRow,
              lastDetailRow,
              summaryRow,
            }) => {
              const expanded = expandedOutlineGroups.has(group.id);
              const buttonSize = 16;
              const buttonLeft = (group.level - 1) * 18 + 4;
              const summaryCenter = layout.rowOffsets[summaryRow]
                + layout.rowHeights[summaryRow] / 2;
              const detailStart = firstDetailRow === undefined
                ? summaryCenter
                : layout.rowOffsets[firstDetailRow]
                  + layout.rowHeights[firstDetailRow] / 2;
              const detailEnd = lastDetailRow === undefined
                ? summaryCenter
                : layout.rowOffsets[lastDetailRow]
                  + layout.rowHeights[lastDetailRow] / 2;
              const lineStart = Math.min(summaryCenter, detailStart);
              const lineEnd = Math.max(summaryCenter, detailEnd);
              return (
                <div key={group.id}>
                  {expanded && lineEnd > lineStart && (
                    <span
                      aria-hidden="true"
                      className="absolute border-l border-[#7a7a7a]"
                      style={{
                        height: lineEnd - lineStart,
                        left: buttonLeft + buttonSize / 2,
                        top: lineStart,
                      }}
                    />
                  )}
                  <button
                    type="button"
                    aria-label={`${expanded ? "Collapse" : "Expand"} row group at row ${group.summary_source_index + 1}`}
                    aria-pressed={expanded}
                    disabled={outlineLoading}
                    onClick={() => onToggleOutlineGroup(group.id)}
                    className="absolute z-10 flex h-4 w-4 items-center justify-center rounded-[2px] border border-[#888] bg-white text-[#303030] shadow-sm hover:bg-[#e8e8e8] disabled:cursor-wait disabled:opacity-60"
                    style={{
                      left: buttonLeft,
                      top: summaryCenter - buttonSize / 2,
                    }}
                  >
                    {expanded ? (
                      <Minus aria-hidden="true" size={11} strokeWidth={1.5} />
                    ) : (
                      <Plus aria-hidden="true" size={11} strokeWidth={1.5} />
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        )}
        <div
          className="flex min-h-full flex-col bg-white"
          style={{
            minWidth: outlineWidth > 0
              ? `calc(100% - ${outlineWidth}px)`
              : "100%",
          }}
        >
          {columnOutlineHeight > 0 && (
            <div
              aria-label="Column outline"
              className="sticky top-0 z-20 shrink-0 border-b border-[#b7b7b7] bg-[#f3f3f3]"
              style={{
                height: columnOutlineHeight,
                minWidth: "100%",
                width: layout.totalColumnWidth,
              }}
            >
              {layout.columnOutlineGroups.map(({
                group,
                firstDetailColumn,
                lastDetailColumn,
                summaryColumn,
              }) => {
                const expanded = expandedColumnOutlineGroups.has(group.id);
                const buttonSize = 16;
                const buttonTop = (group.level - 1) * 18 + 4;
                const summaryCenter = layout.columnOffsets[summaryColumn]
                  + layout.columnWidths[summaryColumn] / 2;
                const detailStart = firstDetailColumn === undefined
                  ? summaryCenter
                  : layout.columnOffsets[firstDetailColumn]
                    + layout.columnWidths[firstDetailColumn] / 2;
                const detailEnd = lastDetailColumn === undefined
                  ? summaryCenter
                  : layout.columnOffsets[lastDetailColumn]
                    + layout.columnWidths[lastDetailColumn] / 2;
                const lineStart = Math.min(summaryCenter, detailStart);
                const lineEnd = Math.max(summaryCenter, detailEnd);
                return (
                  <div key={group.id}>
                    {expanded && lineEnd > lineStart && (
                      <span
                        aria-hidden="true"
                        className="absolute border-t border-[#7a7a7a]"
                        style={{
                          left: lineStart,
                          top: buttonTop + buttonSize / 2,
                          width: lineEnd - lineStart,
                        }}
                      />
                    )}
                    <button
                      type="button"
                      aria-label={`${expanded ? "Collapse" : "Expand"} column group at column ${excelColumnName(group.summary_source_index)}`}
                      aria-pressed={expanded}
                      disabled={outlineLoading}
                      onClick={() => onToggleColumnOutlineGroup(group.id)}
                      className="absolute z-10 flex h-4 w-4 items-center justify-center rounded-[2px] border border-[#888] bg-white text-[#303030] shadow-sm hover:bg-[#e8e8e8] disabled:cursor-wait disabled:opacity-60"
                      style={{
                        left: summaryCenter - buttonSize / 2,
                        top: buttonTop,
                      }}
                    >
                      {expanded ? (
                        <Minus aria-hidden="true" size={11} strokeWidth={1.5} />
                      ) : (
                        <Plus aria-hidden="true" size={11} strokeWidth={1.5} />
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          <div
            role="grid"
            aria-label={sheet.name}
            aria-multiselectable="true"
            aria-rowcount={displayedRowCount}
            aria-colcount={displayedColumnCount}
            style={{
              display: "grid",
              gridTemplateColumns: layout.columnWidths.map((width) => `${width}px`).join(" "),
              gridTemplateRows: layout.rowHeights.map((height) => `${height}px`).join(" "),
              minHeight: `max(${availableGridHeight}, ${Math.max(canvasHeight, 1)}px)`,
              minWidth: "100%",
              width: `max(100%, ${Math.max(canvasWidth, 1)}px)`,
            }}
          >
          {layout.rowHeights.flatMap((_, localRow) =>
            layout.columnWidths.map((__, column) => {
            const row = visibleRowOffset + localRow;
            const key = coordinate(row, column);
            if (layout.mergedChildren.has(key)) return null;
            const cell = layout.cells.get(key);
            const style = layout.styles.get(cell?.style_id ?? 0) ?? DEFAULT_STYLE;
            const merge = layout.mergeAnchors.get(key);
            const frozenRow = row < sheet.frozen_rows;
            const frozenColumn = column < sheet.frozen_columns;
            const selected = isCellInDatabookSelection(
              selectionBounds,
              row,
              column,
            );
            // Show center-across headings as one visual block, retaining the
            // underlying cells for selection and copying.
            const centerRange = layout.centerAcrossRanges.get(key);
            const centerAcrossCells = centerRange?.start === column;
            const allowTextOverflow = centerAcrossCells || canOverflowIntoEmptyCells(cell, style);
            const availableTextWidth = centerAcrossCells
              ? layout.columnOffsets[centerRange.end] + layout.columnWidths[centerRange.end]
                - layout.columnOffsets[column]
              : allowTextOverflow ? textOverflowWidth(row, column, merge) : undefined;
            const visualStyle = cellCss(cell, style, sheet.show_grid_lines, centerAcrossCells);
            if (centerRange) {
              if (column > centerRange.start) visualStyle.borderLeft = "none";
              if (column < centerRange.end) visualStyle.borderRight = "none";
            }
            const selectionStyle: CSSProperties = {
              backgroundImage: selected
                ? "linear-gradient(rgba(33, 115, 70, 0.14), rgba(33, 115, 70, 0.14))"
                : undefined,
              boxShadow: selected && selectionBounds
                ? selectionBoxShadow(selectionBounds, row, column)
                : undefined,
              cursor: "cell",
              userSelect: "none",
            };
            const positionStyle: CSSProperties = {
              gridColumn: merge
                ? `${column + 1} / ${merge.end_column + 2}`
                : column + 1,
              gridRow: merge
                ? `${merge.start_row - visibleRowOffset + 1} / ${
                    merge.end_row - visibleRowOffset + 2
                  }`
                : localRow + 1,
              position: frozenRow || frozenColumn ? "sticky" : undefined,
              top: frozenRow
                ? columnOutlineHeight + layout.rowOffsets[localRow]
                : undefined,
              left: frozenColumn
                ? outlineWidth + layout.columnOffsets[column]
                : undefined,
              zIndex: frozenRow && frozenColumn
                ? allowTextOverflow ? 7 : 6
                : frozenRow || frozenColumn
                  ? allowTextOverflow ? 5 : 4
                  : allowTextOverflow
                    ? 2
                    : 1,
            };
            return (
              <div
                key={key}
                role="gridcell"
                aria-selected={selected}
                data-column={column}
                data-row={row}
                onPointerDown={(event) => handlePointerDown(event, row, column)}
                onPointerEnter={() => handlePointerEnter(row, column)}
                onPointerUp={() => {
                  dragging.current = false;
                }}
                style={{
                  ...visualStyle,
                  ...selectionStyle,
                  ...positionStyle,
                }}
                title={cell?.display_text || undefined}
              >
                <span
                  className={allowTextOverflow
                    ? "overflow-hidden"
                    : "max-w-full overflow-hidden text-ellipsis"}
                  style={allowTextOverflow
                    ? {
                        flexShrink: 0,
                        maxWidth: centerAcrossCells ? Math.max(0, availableTextWidth! - 8) : availableTextWidth,
                        width: centerAcrossCells ? Math.max(0, availableTextWidth! - 8) : "max-content",
                        pointerEvents: centerAcrossCells ? "none" : undefined,
                      }
                    : undefined}
                >
                  {cell?.display_text ?? ""}
                </span>
              </div>
            );
            })
          )}
        </div>
      </div>
      </div>
    </div>
  );
}
